import React, { useRef, useEffect, useState } from "react";
import { useGame } from "../../../context/GameContext";
import { Chess } from "chess.js";
import "./startMenu.scss";
import useAuth from "../../../context/AuthContext";
import { activeRow } from "../../../utils/navigationHelpers";
import { preloadConfettiImages } from "../../../utils/confettiCache";
import { eventStartGame } from "../../../utils/gtag";
import useSoundEffect from "../../../hooks/useSoundEffect";
import { KeyCodes } from "../../../utils/navigationHelpers";
import ChessButton from "../../visual/ChessButton/ChessButton";
import { MultiplayerState, useMultiplayer } from "../../../context/MultiplayerContext";
import ResponsiveBackground from "./ResponsiveBackground";
import { getDailyPuzzle, FALLBACK_PUZZLE } from "../../../utils/puzzleService";
import { savePuzzleAttempt, getTodayDateString } from "../../../utils/puzzleHistoryService";

export default function StartMenu() {
    const {
        gameState,
        setGameState,
        showMultiplayerMenu,
        setShowMultiplayerMenu,
        setRemoteNavigation,
        setStatus,
    } = useGame();

    const { setMultiplayerState } = useMultiplayer();

    const { isAuthenticated, platformAuth } = useAuth();
    const buttonsRef = useRef<(HTMLButtonElement | null)[]>([]);
    const { playClickAudio } = useSoundEffect();
    const [isLoadingPuzzle, setIsLoadingPuzzle] = useState(false);

    useEffect(() => {
        const inCorrectMenu = !gameState.showGame && !showMultiplayerMenu;
        if (inCorrectMenu) {
            const timeout = setTimeout(() => {
                const target = buttonsRef.current[activeRow];
                if (target) target.focus();
            }, 0);
            return () => clearTimeout(timeout);
        }
    }, [gameState.showGame, showMultiplayerMenu]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (KeyCodes.Ups.includes(event.key) || KeyCodes.Downs.includes(event.key)) {
                playClickAudio();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [playClickAudio]);

    /**
     * Will Preload all of the confetti images!
     */
    useEffect(() => {
        preloadConfettiImages();
    }, []);

    /**
     * Start a game against the computer.
     */
    function handlePlayComputerClick() {
        setMultiplayerState((prev: MultiplayerState) => ({ ...prev, playerColor: "w" }));
        const savedGame = localStorage.getItem("ai-chessGameState");
        const chessInstance = savedGame ? new Chess(savedGame) : new Chess();
        setGameState((prev) => ({
            ...prev,
            id: null,
            game: chessInstance,
            isAIGame: true,
            isPuzzle: false,
            aiDifficulty: "Medium",
            showGame: true,
        }));
        setRemoteNavigation((prev) => ({
            ...prev,
            remoteOverSquare: "e2",
            remoteClickSquare: null,
        }));
    }

    /**
     * Handles the multiplayer button click event.
     * Shows the multiplayer menu
     * @param {Event} e the click event from the button.
     */
    async function handleMultiplayerClick(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
        e.preventDefault();
        startNewMultiplayerSession();
    }

    function startNewMultiplayerSession() {
        setStatus({ showStatusPage: true, showLoadingIndicator: true, message: "Loading..." });
        setShowMultiplayerMenu(true);
        setStatus({ showStatusPage: false });
    }

    /**
     * Daily Puzzle
     */

    async function handleDailyPuzzleClick() {
        setIsLoadingPuzzle(true);

        try {
            // Fetch puzzle from API
            const puzzle = await getDailyPuzzle();

            // Use fallback if API fails
            const puzzleData = puzzle || FALLBACK_PUZZLE;

            // Save puzzle to history (count as first attempt since they're starting to play)
            const today = getTodayDateString();
            savePuzzleAttempt(
                today,
                {
                    title: puzzleData.title,
                    fen: puzzleData.fen,
                    pgn: puzzleData.pgn,
                    moves: puzzleData.moves,
                    playerColor: puzzleData.playerColor,
                },
                false,
                0,
                true, // Increment attempts - they're starting to play
            );

            // Create game from FEN (DO NOT apply any moves — start at the FEN)
            const puzzleGame = new Chess(puzzleData.fen);

            // Set the player color based on the puzzle data (player is the side to move in the FEN)
            setMultiplayerState((prev) => ({
                ...prev,
                playerColor: puzzleData.playerColor,
            }));

            setGameState((prev) => ({
                ...prev,
                id: null,
                game: puzzleGame,
                isAIGame: false,
                isPuzzle: true,
                puzzleSolution: puzzleData.moves,
                puzzleIndex: 0, // KEY FIX: start at 0 (player's first move)
                puzzleDate: today, // Track which date's puzzle this is
                showGame: true,
                lastMove: null, // no last move when starting at FEN
            }));

            setRemoteNavigation((prev) => ({
                ...prev,
                remoteOverSquare: "e2",
                remoteClickSquare: null,
            }));

            document.body.style.backgroundImage = "url('images/table2-4k.webp')";
        } catch (error) {
            console.error("Error in handleDailyPuzzleClick:", error);
            alert("Failed to load puzzle");
        } finally {
            setIsLoadingPuzzle(false);
        }
    }

    // Do not show the start menu if the game is already started.
    if (gameState.showGame || showMultiplayerMenu) {
        return null;
    }

    return (
        <div className="chess-menu start" data-testid="start-menu">
            <ResponsiveBackground />
            <img className="chess-logo" src="/images/chess-logo.webp" alt="Chess Logo" />
            <ChessButton
                ref={(el) => (buttonsRef.current[0] = el)}
                data-navcol={0}
                data-navrow={0}
                onClick={() => {
                    handlePlayComputerClick();
                    eventStartGame(1);
                    document.body.style.backgroundImage = "url('images/table2-4k.webp')";
                }}
                data-testid="start-button"
            >
                Play Computer
            </ChessButton>
            <ChessButton
                ref={(el) => (buttonsRef.current[1] = el)}
                data-navcol={0}
                data-navrow={1}
                onClick={async (e) => {
                    await handleMultiplayerClick(e);
                }}
                data-testid="multiplayer-button"
            >
                Play A Friend
            </ChessButton>
            <ChessButton
                ref={(el) => (buttonsRef.current[2] = el)}
                data-navcol={0}
                data-navrow={2}
                data-testid="puzzle-button"
                onClick={async () => {
                    await handleDailyPuzzleClick();
                    document.body.style.backgroundImage = "url('images/table2-4k.webp')";
                }}
                disabled={isLoadingPuzzle}
            >
                {isLoadingPuzzle ? "Loading..." : "Daily Puzzle"}
            </ChessButton>
            {isAuthenticated ? (
                <ChessButton
                    ref={(el) => (buttonsRef.current[3] = el)}
                    data-navcol={0}
                    data-navrow={3}
                    data-testid="logout-button"
                    onClick={() => {
                        platformAuth?.logout();
                    }}
                >
                    Logout
                </ChessButton>
            ) : (
                <ChessButton
                    ref={(el) => (buttonsRef.current[3] = el)}
                    data-navcol={0}
                    data-navrow={3}
                    data-testid="login-button"
                    onClick={() => {
                        platformAuth?.redirectUserToLogin("CHES");
                    }}
                >
                    Login
                </ChessButton>
            )}
            <ChessButton
                ref={(el) => (buttonsRef.current[4] = el)}
                data-navcol={0}
                data-navrow={4}
                onClick={() => {
                    window.location.href = "https://games.sbgtv.tech/";
                }}
                data-testid="quit-button"
            >
                Return To Games
            </ChessButton>
        </div>
    );
}
